import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CreateTicket = () => {
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', customer_address: '', customer_connection_id: '', category: '', description: '', priority: 'medium', isp_ticket_id: '', ticket_type: 'customer', lco_id: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lcos, setLcos] = useState([]);
  const [lcoSearch, setLcoSearch] = useState('');
  const [showLcoDropdown, setShowLcoDropdown] = useState(false);
  const [selectedLcoName, setSelectedLcoName] = useState('');
  const [selectedLcoCode, setSelectedLcoCode] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupMsg, setLookupMsg] = useState('');
  const [customerHistory, setCustomerHistory] = useState(null);

  const handleLookup = async (uid) => {
    if (!uid || uid.length < 3) return;
    setLookupLoading(true);
    setLookupMsg('');
    try {
      const token = localStorage.getItem('token');
      // Use fast local lookup endpoint
      const res = await fetch('/api/crm/local-lookup/' + uid, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const data = await res.json();
      if (data.success && data.customer) {
        const s = data.customer;
        setForm(f => ({
          ...f,
          customer_name: s.name || f.customer_name,
          customer_phone: s.mobile || f.customer_phone,
          customer_address: s.address || f.customer_address,
          lco_id: s.lco_id || f.lco_id,
        }));
        if (s.lco_name) setSelectedLcoName(s.lco_name);
        if (s.lco_name) setLcoSearch(s.lco_name + (s.lco_code ? ' ['+s.lco_code+']' : ''));
        if (s.lco_code) setSelectedLcoCode(s.lco_code);
        setLookupMsg('✅ ' + (s.name||'') + ' | ' + (s.plan||'') + ' | ' + (s.status||'') + (s.expiry?' | Exp: '+s.expiry:''));
        // Fetch ticket history for this customer
        try {
          const token2 = localStorage.getItem('token');
          const hRes = await fetch('/api/tickets?connection_id=' + uid + '&limit=5', { headers: { Authorization: 'Bearer ' + token2 } });
          const hData = await hRes.json();
          setCustomerHistory({ tickets: hData.tickets||[], total: hData.total||0, customer: s });
        } catch(e2) { setCustomerHistory(null); }
      } else {
        setLookupMsg('❌ User not found — please fill customer details manually below');
        setCustomerHistory(null);
      }
    } catch(e) {
      setLookupMsg('❌ Lookup failed: ' + e.message);
    }
    setLookupLoading(false);
  };
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Block subdist_admin from creating tickets
  React.useEffect(() => {
    if (user && user.role === 'subdist_admin') {
      navigate('/tickets');
    }
  }, [user, navigate]);

  // Load LCOs
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/network/lcos', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => setLcos(d.lcos || []))
      .catch(() => {});
  }, []);

  const filteredLcos = lcos.filter(l => {
    if (!lcoSearch) return true;
    const q = lcoSearch.toLowerCase();
    return (l.name||'').toLowerCase().includes(q) ||
           (l.code||'').toLowerCase().includes(q) ||
           (l.area?.area_name||'').toLowerCase().includes(q);
  }).slice(0, 10);
  const primaryColor = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';

  const customerCategories = ['No Internet Connection', 'Slow Speed', 'Router/ONT Issue', 'Cable Cut', 'Frequent Disconnection', 'Billing Issue', 'New Connection', 'Others'];
  const internalCategories = ['Switch Configuration', 'OLT Configuration', 'MSTP Configuration', 'Network Upgrade', 'VLAN Change', 'Port Configuration', 'Fiber Splicing', 'Equipment Installation', 'Internal Request', 'Others'];
  const categories = form.ticket_type === 'internal' ? internalCategories : customerCategories;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await ticketAPI.create(form);
      navigate('/tickets/' + res.data.ticket._id);
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.existing_ticket) {
        setError(`${errData.message} (Ticket: ${errData.existing_ticket} - Status: ${errData.existing_status || 'open'})`);
      } else {
        setError(errData?.message || 'Failed to create ticket');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '6px', color: '#333', fontSize: '14px', fontWeight: '500' };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', color: '#333' }}>Create New Ticket</h2>
      {error && <div style={{ background: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '6px', marginBottom: '20px' }}>{error}</div>}
      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '24px', maxWidth: '800px' }}>
        <form onSubmit={handleSubmit}>
          <h3 style={{ margin: '0 0 16px', color: primaryColor, borderBottom: '2px solid #f0f0f0', paddingBottom: '8px' }}>Customer Information</h3>
          {/* Connection ID lookup - FIRST */}
          <div style={{ marginBottom:'16px', padding:'14px', background:'#f0fdf4', borderRadius:'10px', border:'1px solid #86efac' }}>
            <label style={{...labelStyle, color:'#166534', fontWeight:'700'}}>🔍 Connection ID / User ID</label>
            <div style={{ display:'flex', gap:'8px' }}>
              <input type="text" placeholder="Enter User ID to auto-fill customer details"
                value={form.customer_connection_id}
                onChange={e => setForm({...form, customer_connection_id: e.target.value})}
                onKeyDown={e => e.key==='Enter' && (e.preventDefault(), handleLookup(form.customer_connection_id))}
                style={{...inputStyle, flex:1, fontSize:'15px', fontWeight:'600'}} />
              <button type="button" onClick={() => handleLookup(form.customer_connection_id)}
                disabled={lookupLoading}
                style={{ padding:'10px 20px', background:'#1B6B3A', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'700', whiteSpace:'nowrap' }}>
                {lookupLoading ? '⏳ Loading...' : '🔍 Lookup'}
              </button>
            </div>
            {lookupMsg && <div style={{ marginTop:'8px', fontSize:'13px', color: lookupMsg.startsWith('✅') ? '#166534' : '#dc2626', fontWeight:'600', padding:'6px 10px', background: lookupMsg.startsWith('✅') ? '#dcfce7' : '#fee2e2', borderRadius:'6px' }}>{lookupMsg}</div>}
            {customerHistory && (
              <div style={{ marginTop:'10px', border:'1px solid #e2e8f0', borderRadius:'8px', overflow:'hidden' }}>
                <div style={{ background:'#f8fafc', padding:'8px 12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>📋 Ticket History</div>
                  <div style={{ fontSize:'11px', color: customerHistory.total > 3 ? '#dc2626' : '#64748b', fontWeight: customerHistory.total > 3 ? '700' : '400' }}>
                    {customerHistory.total} total tickets {customerHistory.total > 3 ? '⚠️ Repeat complainant' : ''}
                  </div>
                </div>
                {customerHistory.tickets.length === 0 ? (
                  <div style={{ padding:'10px 12px', fontSize:'12px', color:'#64748b' }}>No previous tickets</div>
                ) : (
                  <div style={{ maxHeight:'180px', overflowY:'auto' }}>
                    {customerHistory.tickets.map((t,i) => (
                      <div key={i} style={{ padding:'8px 12px', borderTop: i>0?'1px solid #f1f5f9':'', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div>
                          <div style={{ fontSize:'11px', fontWeight:'600', color:'#2563eb' }}>{t.ticket_number}</div>
                          <div style={{ fontSize:'11px', color:'#334155', marginTop:'2px' }}>{t.complaint?.category || '-'}</div>
                          <div style={{ fontSize:'10px', color:'#64748b', marginTop:'1px' }}>{new Date(t.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
                        </div>
                        <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'10px', fontWeight:'600', background: t.status==='resolved'||t.status==='closed' ? '#dcfce7' : t.status==='escalated' ? '#fef2f2' : '#fef9c3', color: t.status==='resolved'||t.status==='closed' ? '#15803d' : t.status==='escalated' ? '#dc2626' : '#854d0e' }}>{t.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Customer Name *</label>
              <input type="text" placeholder="Full name" value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} required style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone Number *</label>
              <input type="text" placeholder="Mobile number" value={form.customer_phone} onChange={e => setForm({...form, customer_phone: e.target.value})} required style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Address</label>
            <input type="text" placeholder="Full address" value={form.customer_address} onChange={e => setForm({...form, customer_address: e.target.value})} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>ISP Ticket ID</label>
            <input type="text" placeholder="If from ISP system" value={form.isp_ticket_id} onChange={e => setForm({...form, isp_ticket_id: e.target.value})} style={inputStyle} />
          </div>
          {/* LCO Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>LCO (Local Cable Operator)</label>
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="Type LCO name, code or area to search..."
                  value={lcoSearch}
                  onChange={e => { setLcoSearch(e.target.value); setShowLcoDropdown(true); if(!e.target.value){setForm({...form,lco_id:''});setSelectedLcoName('');} }}
                  onFocus={() => setShowLcoDropdown(true)}
                  style={{ ...inputStyle, flex:1 }}
                />
                {selectedLcoName && (
                  <button type="button" onClick={() => { setForm({...form,lco_id:''}); setSelectedLcoName(''); setLcoSearch(''); setSelectedLcoCode(''); }}
                    style={{ padding:'10px 14px', background:'#fee2e2', color:'#991b1b', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px', whiteSpace:'nowrap' }}>✕ Clear</button>
                )}
              </div>
              {selectedLcoName && (
                <div style={{ marginTop:'6px', padding:'8px 12px', background:'#dcfce7', borderRadius:'6px', fontSize:'13px', color:'#166534', fontWeight:'600' }}>
                  ✅ Selected: {selectedLcoName}{selectedLcoCode && <span style={{marginLeft:'8px',background:'#166534',color:'#fff',padding:'2px 8px',borderRadius:'4px',fontSize:'11px'}}>{selectedLcoCode}</span>}
                </div>
              )}
              {showLcoDropdown && lcoSearch && filteredLcos.length > 0 && !selectedLcoName && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', boxShadow:'0 4px 12px rgba(0,0,0,0.1)', zIndex:100, maxHeight:'200px', overflowY:'auto' }}>
                  {filteredLcos.map(l => (
                    <div key={l._id} onClick={() => {
                      setForm({...form, lco_id: l._id});
                      setSelectedLcoName(l.name + ' (' + l.code + ')');
                      setLcoSearch(l.name + ' (' + l.code + ')');
                      setShowLcoDropdown(false);
                    }} style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #f1f5f9', fontSize:'13px' }}
                    onMouseEnter={e=>e.target.style.background='#f8fafc'}
                    onMouseLeave={e=>e.target.style.background='white'}>
                      <div style={{ fontWeight:'600', color:'#1e293b' }}>{l.name}</div>
                      <div style={{ fontSize:'11px', color:'#94a3b8' }}>{l.code} · {l.area?.area_name||''} · {l.subdistribution_id?.name||''}</div>
                    </div>
                  ))}
                </div>
              )}
              {showLcoDropdown && lcoSearch && filteredLcos.length === 0 && !selectedLcoName && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'12px', zIndex:100, fontSize:'13px', color:'#94a3b8' }}>
                  No LCO found. You can still submit without selecting.
                </div>
              )}
            </div>
          </div>
          <h3 style={{ margin: '0 0 16px', color: primaryColor, borderBottom: '2px solid #f0f0f0', paddingBottom: '8px' }}>Complaint Details</h3>
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
            <button type='button' onClick={() => setForm({...form, ticket_type: 'customer', category: ''})} style={{ flex: 1, padding: '12px', background: form.ticket_type === 'customer' ? primaryColor : 'white', color: form.ticket_type === 'customer' ? 'white' : '#666', border: '2px solid ' + primaryColor, borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>👤 Customer Complaint</button>
            <button type='button' onClick={() => setForm({...form, ticket_type: 'internal', category: ''})} style={{ flex: 1, padding: '12px', background: form.ticket_type === 'internal' ? '#ff9800' : 'white', color: form.ticket_type === 'internal' ? 'white' : '#666', border: '2px solid #ff9800', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>🔧 Internal Work Order</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Category *</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} required style={inputStyle}>
                <option value="">Select category</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority *</label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} style={inputStyle}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Description *</label>
            <textarea placeholder="Describe the complaint in detail..." value={form.description} onChange={e => setForm({...form, description: e.target.value})} required rows={4} style={{...inputStyle, resize: 'vertical'}} />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="submit" disabled={loading} style={{ flex: 1, padding: '12px', background: loading ? '#ccc' : primaryColor, color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Creating...' : 'Create Ticket'}
            </button>
            <button type="button" onClick={() => navigate('/tickets')} style={{ padding: '12px 24px', background: 'white', color: '#333', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicket;
